import { useEffect, useRef, useState } from 'react'

type RecaptchaCheckboxProps = {
  siteKey: string
  onVerify: (token: string) => void
  resetSignal: number
}

type EnterpriseRecaptcha = {
  render: (
    container: HTMLElement,
    options: {
      sitekey: string
      theme?: 'light' | 'dark'
      callback?: (token: string) => void
      'expired-callback'?: () => void
      'error-callback'?: () => void
    },
  ) => number
  reset: (widgetId?: number) => void
}

declare global {
  interface Window {
    grecaptcha?: {
      enterprise?: EnterpriseRecaptcha
    }
  }
}

let recaptchaLoader: Promise<EnterpriseRecaptcha> | null = null

function waitForEnterprise(timeoutMs = 10000): Promise<EnterpriseRecaptcha> {
  return new Promise((resolve, reject) => {
    const started = Date.now()

    const check = () => {
      const enterprise = window.grecaptcha?.enterprise
      if (enterprise?.render) {
        resolve(enterprise)
        return
      }

      if (Date.now() - started >= timeoutMs) {
        reject(new Error('reCAPTCHA Enterprise did not initialize.'))
        return
      }

      window.setTimeout(check, 50)
    }

    check()
  })
}

function loadRecaptchaEnterprise(): Promise<EnterpriseRecaptcha> {
  const enterprise = window.grecaptcha?.enterprise
  if (enterprise?.render) return Promise.resolve(enterprise)
  if (recaptchaLoader) return recaptchaLoader

  const loader = new Promise<EnterpriseRecaptcha>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      'script[data-udhs-recaptcha-enterprise]',
    )

    const ready = () => {
      waitForEnterprise().then(resolve).catch(reject)
    }

    if (existing) {
      ready()
      return
    }

    const script = document.createElement('script')
    script.src = 'https://www.google.com/recaptcha/enterprise.js?render=explicit'
    script.async = true
    script.defer = true
    script.dataset.udhsRecaptchaEnterprise = 'true'
    script.onload = ready
    script.onerror = () => {
      script.remove()
      recaptchaLoader = null
      reject(new Error('reCAPTCHA Enterprise failed to load.'))
    }
    document.head.appendChild(script)
  }).catch((error) => {
    recaptchaLoader = null
    throw error
  })

  recaptchaLoader = loader
  return loader
}

export default function RecaptchaCheckbox({
  siteKey,
  onVerify,
  resetSignal,
}: RecaptchaCheckboxProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const widgetIdRef = useRef<number | null>(null)
  const onVerifyRef = useRef(onVerify)
  const [loadError, setLoadError] = useState('')

  useEffect(() => {
    onVerifyRef.current = onVerify
  }, [onVerify])

  useEffect(() => {
    if (!siteKey || !containerRef.current || widgetIdRef.current !== null) return

    let active = true

    loadRecaptchaEnterprise()
      .then((enterprise) => {
        if (!active || !containerRef.current || widgetIdRef.current !== null) return

        widgetIdRef.current = enterprise.render(containerRef.current, {
          sitekey: siteKey,
          theme: 'light',
          callback: (token) => {
            if (!active) return
            setLoadError('')
            onVerifyRef.current(token)
          },
          'expired-callback': () => {
            if (!active) return
            onVerifyRef.current('')
          },
          'error-callback': () => {
            if (!active) return
            onVerifyRef.current('')
            setLoadError('Security verification could not complete. Please try again.')
          },
        })
      })
      .catch((error) => {
        console.error('reCAPTCHA Enterprise load error:', error)
        if (active) {
          setLoadError('Security verification could not load. Refresh the page and try again.')
        }
      })

    return () => {
      active = false
    }
  }, [siteKey])

  useEffect(() => {
    if (resetSignal <= 0) return

    onVerifyRef.current('')
    const enterprise = window.grecaptcha?.enterprise
    if (enterprise && widgetIdRef.current !== null) {
      try {
        enterprise.reset(widgetIdRef.current)
      } catch (error) {
        console.error('reCAPTCHA Enterprise reset failed:', error)
      }
    }
  }, [resetSignal])

  if (!siteKey) {
    return (
      <p className="recaptcha-message">
        Security verification is not configured yet.
      </p>
    )
  }

  return (
    <div className="recaptcha-wrap">
      <div ref={containerRef} />
      {loadError ? <p className="recaptcha-message">{loadError}</p> : null}
    </div>
  )
}
